import moment from 'moment';

import * as DatetimeFields from 'notebook/data-tables/DatetimeFields';
import ArgType from 'notebook/tables/excess/ArgType';
import FunctionCategory from 'notebook/tables/excess/FunctionCategory';
import {EvaluablePrimitiveSpreadsheetFunction} from 'notebook/tables/excess/FunctionConstants';
import * as ExcessUtil from 'notebook/tables/excess/Util';
import {BaseTypedValueType, TypedValue} from 'notebook/tables/TableConstants';
import {coerceToPrimitive} from 'notebook/tables/typed-values/DataTypes';
import {FieldType} from 'schemas/fields/SchemaFieldConstants';

const DATE_COERCABLE_TYPED_VALUE_TYPES: Array<string> = [
  FieldType.TEXT,
  FieldType.LONG_TEXT,
  BaseTypedValueType.STRING,
];

// Minutes per day, used to express a UTC offset as a fraction of a day.
const MINUTES_PER_DAY = 1440;

// Milliseconds per day. Inputs carry at most millisecond precision (moment's resolution) and offsets are
// whole minutes, so this is the finest meaningful granularity of a day difference; it's the unit we round to
// when clearing float noise (see eval).
const MS_PER_DAY = MINUTES_PER_DAY * 60 * 1000;

// The date that has serial number 0. Anchored in UTC so that the date->serial conversion below depends only
// on a value's wall-clock fields and explicit offset, never on the timezone the evaluator process happens to
// run in, keeping the result stable across evaluators in different timezones.
const EPOCH_ZERO_DATE = moment.utc('1899-12-30');

// Matches a trailing UTC offset (Z, ±hh:mm, or ±hhmm). moment's ISO_8601 parser does not report whether an
// offset was present via creationData(), so we detect it on the raw string instead.
const TRAILING_OFFSET_REGEX = /([Zz]|[+-]\d{2}:?\d{2})$/;

// The primitive form a DayValue takes when crossing the coerce()->eval() boundary: [epoch, utcOffsetMinutes].
// A null offset is encoded as NaN (real offsets are always finite) so the tuple is an Array<number>, which is
// already an EvaluablePrimitive — letting DAYS use the shared function types unchanged instead of widening
// them to carry a custom object.
type DayValueTuple = [epoch: number, utcOffsetMinutes: number];

// The single representation DAYS coerces every argument into.
//   - `epoch` is days since EPOCH_ZERO_DATE, computed from the value's wall-clock fields read in UTC (i.e.
//     before any UTC offset is applied). For a bare number argument — an Excel-style serial — this is just
//     the number itself.
//   - `utcOffsetMinutes` is the value's explicit UTC offset in minutes, or null when it had none (a plain
//     date, a local datetime, or a raw serial). eval() applies it (or a borrowed one) to place the value on
//     the day axis.
// Computing `epoch` from wall-clock fields in UTC keeps the conversion independent of the timezone the
// evaluator runs in; a raw number flows through untouched, so large/fractional/negative serials keep working.
// coerce() returns the tuple form and eval() rebuilds the DayValue, so the arithmetic reads off named fields.
class DayValue {
  constructor(
    readonly epoch: number,
    readonly utcOffsetMinutes: number | null
  ) {}

  toTuple(): DayValueTuple {
    return [this.epoch, this.utcOffsetMinutes ?? NaN];
  }

  static fromTuple([epoch, utcOffsetMinutes]: DayValueTuple): DayValue {
    return new DayValue(epoch, Number.isNaN(utcOffsetMinutes) ? null : utcOffsetMinutes);
  }
}

function momentToDayValue(parsed: moment.Moment, hasOffset: boolean): DayValue {
  // Reinterpret the value's wall-clock reading in UTC, keeping the displayed fields and discarding the
  // offset (keepLocalTime), so the serial depends only on those fields and never on the evaluator's
  // timezone. The offset is preserved separately in `utcOffsetMinutes` for eval() to apply or lend to its
  // sibling.
  const wallClockInUtc = parsed.clone().utcOffset(0, /* keepLocalTime */ true);
  return new DayValue(
    wallClockInUtc.diff(EPOCH_ZERO_DATE, 'days', /* asFloat */ true),
    hasOffset ? parsed.utcOffset() : null
  );
}

// Parse an ISO 8601 date/datetime string (the form ft_date/ft_datetime cells always store) into a DayValue,
// preserving any explicit offset. Returns null if the value is absent or isn't ISO 8601.
function parseIsoDayValue(value: string | null | undefined): DayValue | null {
  if (value == null) {
    return null;
  }
  const trimmed = value.trim();
  const parsed = moment.parseZone(trimmed, moment.ISO_8601, /* strict */ true);
  if (!parsed.isValid()) {
    return null;
  }
  return momentToDayValue(parsed, TRAILING_OFFSET_REGEX.test(trimmed));
}

// Parse a TEXT cell's contents as a date using the user's locale (so e.g. "3/4/2021" honors month/day
// ordering), preserving an explicit offset when one is typed. Returns null if it isn't a date.
function parseLocaleDayValue(value: string, languageTag?: string | null): DayValue | null {
  const parsed = DatetimeFields.tryParseAsZonedMoment(value, languageTag);
  return parsed == null ? null : momentToDayValue(parsed.moment, parsed.hasOffset);
}

// Parse a date/datetime typed value's stored ISO string directly into a DayValue, or null if it isn't a
// date/datetime type. Unlike coerceToPrimitive's date handling, this does NOT reinterpret the instant in the
// evaluator's timezone, so the stored ISO value (e.g. "1970-01-01T00:00:00-08:00") is parsed exactly as
// authored, down to moment's millisecond resolution (finer fractional seconds are truncated to ms).
function dateTypedValueToDayValue(typedValue: TypedValue): DayValue | null {
  switch (typedValue.type) {
    case FieldType.DATE:
      return parseIsoDayValue(typedValue.dateValue ?? typedValue.value);
    case FieldType.DATETIME:
      return parseIsoDayValue(typedValue.datetimeValue ?? typedValue.value);
    case BaseTypedValueType.DATE:
    case BaseTypedValueType.DATETIME:
      return parseIsoDayValue(typedValue.value);
    default:
      return null;
  }
}

// Coerce a single DAYS argument to a DayValue: a parsed date, or a bare number (an Excel-style serial) carried
// as a DayValue with no offset. All locale-dependent parsing happens here, where languageTag is available.
function toDayValue(typedValue: TypedValue, languageTag?: string | null): DayValue | null {
  const dateDayValue = dateTypedValueToDayValue(typedValue);
  if (dateDayValue != null) {
    return dateDayValue;
  }
  const result = coerceToPrimitive(typedValue, {shouldCoerceStringToNumber: true, languageTag});
  if (typeof result === 'number') {
    // A raw serial is already an epoch value; it has no offset to borrow or donate.
    return new DayValue(result, null);
  }
  // A text cell that isn't numeric: try to parse it as a date in the user's locale. The type gate keeps us
  // from treating the display string of a non-text value (e.g. a bioentity named "2025-01-01") as a date.
  if (typeof result === 'string' && DATE_COERCABLE_TYPED_VALUE_TYPES.includes(typedValue.type as string)) {
    return parseLocaleDayValue(result, languageTag);
  }
  return null;
}

// coerce() hands eval() the DayValue's primitive tuple form (or null when the argument isn't a date/number);
// eval() rebuilds the DayValue from it.
function coerce(typedValue: TypedValue, languageTag?: string | null): DayValueTuple | null {
  const dayValue = toDayValue(typedValue, languageTag);
  return dayValue == null ? null : dayValue.toTuple();
}

export default {
  name: 'DAYS',
  category: FunctionCategory.TIMESTAMP,
  shortDescription: 'Number of days between two dates.',
  description: 'Returns the number of days between two dates.',
  params: [
    {
      name: 'end_date',
      type: ArgType.any,
      description: 'The end date.',
      coerce,
    },
    {
      name: 'start_date',
      type: ArgType.any,
      description: 'The start date.',
      coerce,
    },
  ],
  returnType: ExcessUtil.ValueType.NUMBER,
  eval: (endTuple: DayValueTuple | null, startTuple: DayValueTuple | null): number => {
    if (endTuple == null || startTuple == null) {
      throw new ExcessUtil.ValueError('Both start and end dates must be valid dates or numbers');
    }
    const end = DayValue.fromTuple(endTuple);
    const start = DayValue.fromTuple(startTuple);
    // Each side uses its own UTC offset, falling back to a single shared offset when it has none, so both
    // operands sit in the same frame of reference. That shared offset is whichever side carried one (or 0 if
    // neither did); when both carry their own, each keeps it and the time-of-day difference is preserved.
    // Applying the offset converts the wall-clock epoch into the actual instant on the day axis.
    const sharedOffset = end.utcOffsetMinutes ?? start.utcOffsetMinutes ?? 0;
    const endEpoch = end.epoch - (end.utcOffsetMinutes ?? sharedOffset) / MINUTES_PER_DAY;
    const startEpoch = start.epoch - (start.utcOffsetMinutes ?? sharedOffset) / MINUTES_PER_DAY;
    // The utcOffsetMinutes/1440 division can leave float noise just below an integer (e.g. a whole-day
    // difference comes out as 20602.99999999998), which would floor to an off-by-one. Snap to the millisecond
    // the inputs actually carry before flooring: that clears the sub-millisecond noise while preserving any
    // genuine sub-day difference (>= 1ms).
    return Math.floor(Math.round((endEpoch - startEpoch) * MS_PER_DAY) / MS_PER_DAY);
  },
} satisfies EvaluablePrimitiveSpreadsheetFunction;
